import { useState } from "react";

import { Collapse, Stack, Typography } from "@mui/material";
import Card from "components/elements/Card";
import { BarLineChart, ChevronDown } from "components/icons";
import LinkedStrategyCard from "components/modules/LinkedStrategyCard";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ILinkedGridBot } from "types/types";
import { getLocalStorageBooleanValue } from "utils";

interface IProps {
  linkedBots: ILinkedGridBot[];
  currencyPair: string;
}

const LinkedGridStrategies = ({ linkedBots, currencyPair }: IProps) => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const navigate = useNavigate();
  const [inactiveExpanded, setInactiveExpanded] = useState<boolean>(
    getLocalStorageBooleanValue("dcaDisabledLinkedBotsExpanded", true)
  );

  const handleInactiveExpanded = () => {
    localStorage.setItem("dcaDisabledLinkedBotsExpanded", (!inactiveExpanded).toString());
    setInactiveExpanded((prev) => !prev);
  };

  const renderBot = (bot: ILinkedGridBot) => {
    const statusColor = bot.enabled ? colors.success : colors.error;

    return (
      <LinkedStrategyCard
        key={bot.id}
        color={statusColor}
        icon={<BarLineChart />}
        exchangeTitle={bot.exchangeTitle}
        exchangeConnectionLabel={bot.exchangeConnectionLabel}
        currencyPair={currencyPair}
        label={bot.label}
        detailLabel={t("dcaBots.linkedGridStrategies.detail")}
        onDetail={() => navigate(`/grid-strategie/${bot.id}`)}
      />
    );
  };

  const activeBots = linkedBots.filter((bot) => bot.enabled);
  const inactiveBots = linkedBots.filter((bot) => !bot.enabled);

  const renderGroup = (bots: ILinkedGridBot[], label: string, color: string, collapsible = false) => {
    if (!bots.length) return null;

    const expanded = !collapsible || inactiveExpanded;

    return (
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          onClick={collapsible ? handleInactiveExpanded : undefined}
          sx={{ color, ...(collapsible ? { cursor: "pointer", alignSelf: "flex-start" } : {}) }}>
          <Typography variant="body2" fontWeight={600} color="inherit">
            {label}
          </Typography>
          {collapsible && (
            <ChevronDown
              sx={{
                fontSize: 20,
                transform: inactiveExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          )}
        </Stack>
        <Collapse in={expanded}>
          <Stack spacing={1} pt={1}>
            {bots.map(renderBot)}
          </Stack>
        </Collapse>
      </Stack>
    );
  };

  return (
    <Card header={t("dcaBots.linkedGridStrategies.title")} sx={{ mb: 0 }}>
      <Stack spacing={2}>
        {renderGroup(activeBots, t("dcaBots.linkedGridStrategies.active"), colors.success)}
        {renderGroup(inactiveBots, t("dcaBots.linkedGridStrategies.inactive"), colors.error, true)}
      </Stack>
    </Card>
  );
};

export default LinkedGridStrategies;
